import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react'
import usePopover, { panelStyle } from './usePopover'
import { CalendarMonth } from './Calendar'
import {
  addMonths,
  iso,
  maskTyped,
  monthLabel,
  monthName,
  moveDay,
  parseIso,
  parseTyped,
  sameDay,
  sameMonth,
  startOfMonth,
  today as todayDate,
} from './calendarDates'
import { kit } from './strings'
import { displayDate } from '../lib/dates'
import './DatePicker.css'

/**
 * A date field with a calendar of our own.
 *
 * `<input type="date">` hands the picker to the OS: a Chrome-drawn panel in the system
 * font with a US-format mask, no way to style a day, and — on an Arabic-first screen —
 * month names and a week that start wherever the browser decides. The calendar here is
 * drawn in the app's type, spacing and accent, with the week starting on Saturday.
 *
 * TYPED OR PICKED. The field is a text input in the shape the screen prints dates
 * (`DD-MM-YYYY`, the dashes put in as you type), so a known date is eight keystrokes
 * instead of a walk through months; the calendar opens on a click, or on ↓ from the
 * field. In the calendar: arrows by day and week, Home/End to the week's edges,
 * PageUp/PageDown by month (with Shift, by year), Enter to take, Escape to leave. The
 * month title opens a month grid for long jumps.
 *
 * The value stays ISO `YYYY-MM-DD` in and out, so nothing that consumes it changes.
 *
 * @param {string} value      ISO `YYYY-MM-DD`, empty for unset
 * @param {(value: string) => void} onChange
 * @param {string} [min] {string} [max]  ISO bounds; days outside are unpickable
 * @param {'field'|'bare'} [variant]  `bare` is a click-to-pick trigger with no shell,
 *   for a control already inside one (the date half of DateTimePicker)
 * @param {boolean} [clearable]  show a clear button once a date is set. OPT-IN,
 *   because for most fields "no date" is not a meaningful answer — but where the
 *   endpoint reads an empty value as an instruction of its own (a null unlocks an
 *   account, cancels a resignation, removes an expiry) the picker has to be able
 *   to get BACK to empty, which a calendar alone cannot do.
 */
export default function DatePicker({
  id,
  value,
  onChange,
  clearable = false,
  /* The FORMAT, not the field's name: the label above already names it. */
  placeholder = 'DD-MM-YYYY',
  min,
  max,
  disabled = false,
  invalid = false,
  variant = 'field',
  className = '',
  ...rest
}) {
  /* What is being typed, until it is a date or the field is left. */
  const [draft, setDraft] = useState(null)
  const { open, setOpen, close, pos, rootRef, panelRef, id: panelId } = usePopover({
    align: 'start',
    /* However the calendar closes, a half-typed date does not outlive it. */
    onClose: () => setDraft(null),
  })
  const inputRef = useRef(null)

  const selected = useMemo(() => parseIso(value), [value])
  const lower = useMemo(() => parseIso(min), [min])
  const upper = useMemo(() => parseIso(max), [max])
  const blocked = (day) => Boolean((lower && day < lower) || (upper && day > upper))

  const [view, setView] = useState(() => startOfMonth(selected ?? todayDate()))
  const [focusDay, setFocusDay] = useState(null)
  /* Whether the grid should hold the focus — true once the calendar is driven from the
     keyboard, false while the caret is in the field and the calendar only follows it. */
  const [inGrid, setInGrid] = useState(false)
  const [mode, setMode] = useState('days')

  // Every opening starts on the value's month, in the day grid.
  useEffect(() => {
    if (!open) return
    const anchor = selected ?? todayDate()
    setView(startOfMonth(anchor))
    setFocusDay(anchor)
    setMode('days')
  }, [open, selected])

  const trigger = () =>
    variant === 'field' ? inputRef.current : rootRef.current?.querySelector('.datepicker__control')

  const openCalendar = (withKeyboard) => {
    setInGrid(withKeyboard)
    setOpen(true)
  }

  const leave = () => {
    close()
    setInGrid(false)
    trigger()?.focus()
  }

  const take = (day) => {
    if (blocked(day)) return
    onChange?.(iso(day))
    setDraft(null)
    leave()
  }

  /* ---- the typed path ---- */

  const typed = draft != null ? parseTyped(draft) : null
  const draftBad = draft != null && draft.length === 10 && (!typed || blocked(typed))

  const onType = (event) => {
    const next = maskTyped(event.target.value)
    setDraft(next)
    const date = parseTyped(next)
    if (next.length === 10 && date && !blocked(date)) {
      onChange?.(iso(date))
      setView(startOfMonth(date))
      setFocusDay(date)
    }
  }

  const onFieldKey = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      openCalendar(true)
    } else if (event.key === 'Enter') {
      /* Confirms the date; it must not also submit the form around it. */
      event.preventDefault()
      setDraft(null)
      close()
    } else if (event.key === 'Escape' && (open || draft != null)) {
      event.stopPropagation()
      setDraft(null)
      close()
    }
  }

  /* Leaving the field settles it: a whole date was already taken as it was typed,
     an emptied field clears (where clearing is allowed), and anything else falls back
     to the value — a half-typed date is not a value. */
  const onFieldBlur = (event) => {
    const to = event.relatedTarget
    if (to && (rootRef.current?.contains(to) || panelRef.current?.contains(to))) return
    if (draft === '' && clearable) onChange?.('')
    setDraft(null)
    close()
  }

  /* ---- the calendar ---- */

  const onGridKey = (event) => {
    const from = focusDay ?? selected ?? todayDate()
    const next = moveDay(from, event)
    if (!next) return
    event.preventDefault()
    setInGrid(true)
    setFocusDay(next)
    if (!sameMonth(next, view)) setView(startOfMonth(next))
  }

  const onPanelKey = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      leave()
    } else if (event.key === 'Tab') {
      event.preventDefault()
      leave()
    }
  }

  const pickMonth = (monthIndex) => {
    const target = new Date(view.getFullYear(), monthIndex, 1)
    const last = new Date(target.getFullYear(), monthIndex + 1, 0).getDate()
    setView(target)
    setFocusDay(new Date(target.getFullYear(), monthIndex, Math.min((focusDay ?? target).getDate(), last)))
    setMode('days')
  }

  const now = todayDate()
  const showClear = clearable && !!value && !disabled
  const shownText = draft ?? (value ? displayDate(value) : '')
  const isInvalid = invalid || draftBad

  const panel =
    open &&
    createPortal(
      <div
        ref={panelRef}
        id={panelId}
        role="dialog"
        aria-label={kit.calendar}
        className="datepicker__panel"
        style={panelStyle(pos)}
        onKeyDown={onPanelKey}
      >
        <div className="datepicker__head">
          {/* In RTL "previous" points right and "next" points left, so the glyphs are
              chosen rather than mirrored. */}
          <button
            type="button"
            className="datepicker__nav"
            tabIndex={-1}
            onClick={() => setView((month) => addMonths(month, mode === 'days' ? -1 : -12))}
            aria-label={mode === 'days' ? monthLabel.format(addMonths(view, -1)) : String(view.getFullYear() - 1)}
          >
            <ChevronRight className="ltr-flip" size={16} strokeWidth={2} />
          </button>
          <button
            type="button"
            className={`datepicker__title${mode === 'months' ? ' is-open' : ''}`}
            tabIndex={-1}
            onClick={() => setMode((current) => (current === 'days' ? 'months' : 'days'))}
            aria-label={`${monthLabel.format(view)} — ${kit.chooseMonth}`}
          >
            {mode === 'days' ? monthLabel.format(view) : view.getFullYear()}
          </button>
          <button
            type="button"
            className="datepicker__nav"
            tabIndex={-1}
            onClick={() => setView((month) => addMonths(month, mode === 'days' ? 1 : 12))}
            aria-label={mode === 'days' ? monthLabel.format(addMonths(view, 1)) : String(view.getFullYear() + 1)}
          >
            <ChevronLeft className="ltr-flip" size={16} strokeWidth={2} />
          </button>
        </div>

        {mode === 'days' ? (
          <CalendarMonth
            view={view}
            focusDay={focusDay}
            /* Not before the panel is placed: it is hidden until then, and a hidden
               button cannot take the focus. */
            autoFocus={inGrid && Boolean(pos)}
            onKeyDown={onGridKey}
            onPick={take}
            dayState={(day) => ({ selected: sameDay(day, selected), blocked: blocked(day) })}
          />
        ) : (
          <div className="datepicker__months">
            {Array.from({ length: 12 }, (_, monthIndex) => {
              const month = new Date(view.getFullYear(), monthIndex, 1)
              const lastDay = new Date(view.getFullYear(), monthIndex + 1, 0)
              const out = (lower && lastDay < lower) || (upper && month > upper)
              return (
                <button
                  key={monthIndex}
                  type="button"
                  className={[
                    'datepicker__month-cell',
                    selected && sameMonth(month, selected) && 'is-selected',
                    sameMonth(month, now) && 'is-current',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  disabled={Boolean(out)}
                  onClick={() => pickMonth(monthIndex)}
                >
                  {monthName.format(month)}
                </button>
              )
            })}
          </div>
        )}

        <div className="datepicker__foot">
          <button
            type="button"
            className="datepicker__link"
            tabIndex={-1}
            disabled={blocked(now)}
            onClick={() => take(now)}
          >
            {kit.today}
          </button>
          {showClear && (
            <button
              type="button"
              className="datepicker__link datepicker__link--quiet"
              tabIndex={-1}
              onClick={() => {
                onChange?.('')
                setDraft(null)
                leave()
              }}
            >
              {kit.clearDate}
            </button>
          )}
        </div>
      </div>,
      document.body,
    )

  if (variant === 'bare') {
    return (
      <div className={`datepicker datepicker--bare ${className}`.trim()} ref={rootRef}>
        <button
          id={id}
          type="button"
          className={`datepicker__control${open ? ' is-open' : ''}${value ? '' : ' is-empty'}${
            invalid ? ' is-invalid' : ''
          }`}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          onClick={() => (open ? close() : openCalendar(false))}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault()
              openCalendar(true)
            }
          }}
          aria-haspopup="dialog"
          aria-expanded={open}
          {...rest}
        >
          <span className="datepicker__value" dir={value ? 'ltr' : undefined}>
            {value ? displayDate(value) : placeholder}
          </span>
        </button>
        {panel}
      </div>
    )
  }

  return (
    <div className={`datepicker datepicker--field ${className}`.trim()} ref={rootRef}>
      <div
        className={[
          'datepicker__shell',
          open && 'is-open',
          isInvalid && 'is-invalid',
          disabled && 'is-disabled',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <input
          ref={inputRef}
          id={id}
          type="text"
          dir="ltr"
          inputMode="numeric"
          autoComplete="off"
          className="datepicker__input"
          value={shownText}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={isInvalid || undefined}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          onChange={onType}
          onClick={() => !open && openCalendar(false)}
          onKeyDown={onFieldKey}
          onBlur={onFieldBlur}
          {...rest}
        />
        {/* The glyph opens the calendar for a pointer; the keyboard has ↓ in the field,
            so it is not a second tab stop. */}
        <button
          type="button"
          className="datepicker__toggle"
          tabIndex={-1}
          disabled={disabled}
          aria-label={kit.calendar}
          onClick={() => (open ? close() : openCalendar(true))}
        >
          <CalendarDays size={16} strokeWidth={1.75} />
        </button>
        {showClear && (
          <button
            type="button"
            className="datepicker__clear"
            onClick={() => {
              onChange?.('')
              setDraft(null)
              inputRef.current?.focus()
            }}
            aria-label={kit.clearDate}
          >
            <X size={14} strokeWidth={2} />
          </button>
        )}
      </div>
      {panel}
    </div>
  )
}
